import { useEffect, useMemo, useState } from 'react';
import { Activity, Cable, ChevronDown, ChevronRight, Copy, History, Mail, Plus, RefreshCw, Save, Search, Trash2, Webhook, X } from 'lucide-react';
import { automationService } from '../services/automationService';
import { ConnectionProfiles } from '../components/ConnectionProfiles';
import { connectionProfileService } from '../services/connectionProfileService';

type ConditionRow = {
  id: string;
  source: 'after' | 'before';
  field: string;
  operator: 'equals' | 'not_equals' | 'contains' | 'exists' | 'not_exists' | 'changed' | 'changed_to' | 'changed_from';
  value: string;
};

type ConditionGroup = {
  id: string;
  logic: 'AND' | 'OR';
  conditions: ConditionRow[];
};

type WebhookAction = {
  id: string;
  type: 'webhook';
  name: string;
  profileId: string;
  method: 'POST' | 'PUT' | 'PATCH' | 'GET';
  url: string;
  headersText: string;
  bodyText: string;
};

type EmailAction = {
  id: string;
  type: 'email';
  name: string;
  profileId: string;
  to: string;
  cc: string;
  bcc: string;
  subject: string;
  body: string;
  isHtml: boolean;
};

type ActionRow = WebhookAction | EmailAction;

type AutomationForm = {
  id?: string;
  name: string;
  description: string;
  entityType: 'shipment' | 'customer' | 'warehouse' | 'expense' | 'traffic-fine' | 'support';
  triggerEvent: string;
  isActive: boolean;
  conditions: ConditionGroup[];
  actions: ActionRow[];
};

const entityOptions = [
  { value: 'shipment', label: 'Sevkiyat / Sipariş' },
  { value: 'customer', label: 'Müşteri' },
  { value: 'warehouse', label: 'Depo' },
  { value: 'expense', label: 'Masraf' },
  { value: 'traffic-fine', label: 'Trafik Cezası' },
  { value: 'support', label: 'Destek Talebi' },
];

const conditionFieldsByEntity: Record<string, { value: string; label: string }[]> = {
  shipment: [
    { value: 'status', label: 'Durum' },
    { value: 'process_status', label: 'İşlem Durumu' },
    { value: 'accounting_status', label: 'Muhasebe Durumu' },
    { value: 'pickup_city', label: 'Yükleme İli' },
    { value: 'delivery_city', label: 'Boşaltma İli' },
    { value: 'pickup_name', label: 'Yükleme Müşterisi' },
    { value: 'delivery_name', label: 'Boşaltma Müşterisi' },
    { value: 'shipment_no', label: 'Sevk Kodu' },
    { value: 'assigned_driver_id', label: 'Sürücü Ataması' },
    { value: 'assigned_vehicle_id', label: 'Araç Ataması' },
  ],
  customer: [
    { value: 'status', label: 'Durum' },
    { value: 'company_name', label: 'Firma Adı' },
    { value: 'city', label: 'Şehir' },
    { value: 'customer_type', label: 'Müşteri Tipi' },
    { value: 'kanal_tipi', label: 'Kanal Tipi' },
  ],
  warehouse: [
    { value: 'status', label: 'Durum' },
    { value: 'name', label: 'Depo Adı' },
    { value: 'city', label: 'Şehir' },
    { value: 'manager_name', label: 'Yönetici' },
  ],
  expense: [
    { value: 'status', label: 'Durum' },
    { value: 'category', label: 'Kategori' },
    { value: 'amount', label: 'Tutar' },
    { value: 'description', label: 'Açıklama' },
  ],
  'traffic-fine': [
    { value: 'status', label: 'Durum' },
    { value: 'process_status', label: 'İşlem Durumu' },
    { value: 'fine_amount', label: 'Ceza Tutarı' },
    { value: 'vehicle_plate', label: 'Plaka' },
    { value: 'fine_type', label: 'Ceza Türü' },
  ],
  support: [
    { value: 'status', label: 'Durum' },
    { value: 'priority', label: 'Öncelik' },
    { value: 'subject', label: 'Konu' },
    { value: 'category', label: 'Kategori' },
  ],
};

const triggerOptionsByEntity: Record<string, { value: string; label: string }[]> = {
  shipment: [
    { value: 'created', label: 'Kayıt Oluşturuldu' },
    { value: 'updated', label: 'Kayıt Güncellendi' },
    { value: 'status-changed', label: 'Durum Değişti' },
    { value: 'process-status-changed', label: 'İşlem Durumu Değişti' },
    { value: 'accounting-status-changed', label: 'Muhasebe Durumu Değişti' },
    { value: 'assignment-changed', label: 'Araç / Sürücü Ataması Değişti' },
  ],
  customer: [
    { value: 'created', label: 'Kayıt Oluşturuldu' },
    { value: 'updated', label: 'Kayıt Güncellendi' },
  ],
  warehouse: [
    { value: 'created', label: 'Kayıt Oluşturuldu' },
    { value: 'updated', label: 'Kayıt Güncellendi' },
  ],
  expense: [
    { value: 'created', label: 'Kayıt Oluşturuldu' },
    { value: 'updated', label: 'Kayıt Güncellendi' },
  ],
  'traffic-fine': [
    { value: 'created', label: 'Kayıt Oluşturuldu' },
    { value: 'updated', label: 'Kayıt Güncellendi' },
  ],
  support: [
    { value: 'created', label: 'Kayıt Oluşturuldu' },
    { value: 'updated', label: 'Kayıt Güncellendi' },
    { value: 'status-changed', label: 'Durum Değişti' },
  ],
};

const defaultForm = (): AutomationForm => ({
  name: 'Yeni Otomasyon',
  description: '',
  entityType: 'shipment',
  triggerEvent: 'status-changed',
  isActive: true,
  conditions: [],
  actions: [],
});

const createConditionGroup = (): ConditionGroup => ({
  id: `group-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  logic: 'AND',
  conditions: [],
});

const createCondition = (): ConditionRow => ({
  id: `cond-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  source: 'after',
  field: 'status',
  operator: 'equals',
  value: '',
});

const createWebhookAction = (): WebhookAction => ({
  id: `action-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  type: 'webhook',
  name: 'Webhook Aksiyonu',
  profileId: '',
  method: 'POST',
  url: '',
  headersText: '{\n  "Content-Type": "application/json"\n}',
  bodyText: '{\n  "shipmentNo": "{{after.shipment_no}}",\n  "status": "{{after.status}}"\n}',
});

const createEmailAction = (): EmailAction => ({
  id: `action-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  type: 'email',
  name: 'Mail Aksiyonu',
  profileId: '',
  to: '',
  cc: '',
  bcc: '',
  subject: 'Sevkiyat Bilgilendirmesi - {{after.shipment_no}}',
  body: '<!DOCTYPE html>\n<html lang="tr">\n<head>\n  <meta charset="UTF-8">\n  <title>Sevkiyat Bilgilendirmesi</title>\n</head>\n<body style="margin:0; padding:24px; background:#f8fafc; font-family:Arial,sans-serif;">\n  <div style="max-width:640px; margin:0 auto; background:#ffffff; border:1px solid #e2e8f0; border-radius:12px; padding:24px;">\n    <h2 style="margin:0 0 16px; color:#0f172a;">Yeni Sevkiyat Bildirimi</h2>\n    <table style="width:100%; border-collapse:collapse; font-size:14px;">\n      <tr>\n        <td style="padding:8px; border:1px solid #cbd5e1;"><b>Sevk Kodu</b></td>\n        <td style="padding:8px; border:1px solid #cbd5e1;">{{after.shipment_no}}</td>\n      </tr>\n      <tr>\n        <td style="padding:8px; border:1px solid #cbd5e1;"><b>Durum</b></td>\n        <td style="padding:8px; border:1px solid #cbd5e1;">{{after.status}}</td>\n      </tr>\n      <tr>\n        <td style="padding:8px; border:1px solid #cbd5e1;"><b>Yükleme</b></td>\n        <td style="padding:8px; border:1px solid #cbd5e1;">{{after.pickup_name}}</td>\n      </tr>\n      <tr>\n        <td style="padding:8px; border:1px solid #cbd5e1;"><b>Yükleme İlçesi</b></td>\n        <td style="padding:8px; border:1px solid #cbd5e1;">{{after.pickup_district}}</td>\n      </tr>\n      <tr>\n        <td style="padding:8px; border:1px solid #cbd5e1;"><b>Boşaltma</b></td>\n        <td style="padding:8px; border:1px solid #cbd5e1;">{{after.delivery_name}}</td>\n      </tr>\n    </table>\n    <div style="margin-top:20px; text-align:center;">\n      <a href="{{detail_url}}" style="display:inline-block; background:#0f172a; color:#ffffff; text-decoration:none; padding:12px 18px; border-radius:8px; font-weight:700;">Kayıt Detayını Aç</a>\n    </div>\n  </div>\n</body>\n</html>',
  isHtml: true,
});

const mapApiToForm = (row: any): AutomationForm => ({
  id: String(row.id),
  name: row.name || '',
  description: row.description || '',
  entityType: row.entity_type || 'shipment',
  triggerEvent: row.trigger_event || 'status-changed',
  isActive: Boolean(row.is_active),
  conditions: Array.isArray(row.conditions) ? (row.conditions.length > 0 && 'logic' in row.conditions[0] && 'conditions' in row.conditions[0]
    ? row.conditions.map((group: any, gi: number) => ({ id: group.id || `group-${gi}`, logic: group.logic === 'OR' ? 'OR' : 'AND', conditions: Array.isArray(group.conditions) ? group.conditions.map((condition: any, ci: number) => ({ id: condition.id || `cond-${gi}-${ci}`, source: condition.source || 'after', field: condition.field || 'status', operator: condition.operator || 'equals', value: condition.value || '' })) : [] }))
    : [{ id: 'group-0', logic: 'AND' as const, conditions: row.conditions.map((condition: any, index: number) => ({ id: condition.id || `cond-0-${index}`, source: condition.source || 'after', field: condition.field || 'status', operator: condition.operator || 'equals', value: condition.value || '' })) }]
  ) : [],
  actions: Array.isArray(row.actions) ? row.actions.map((action: any, index: number) => action.type === 'email'
    ? ({ id: action.id || `action-${index}`, type: 'email', name: action.name || 'Mail Aksiyonu', profileId: String(action.profile_id || ''), to: action.to || '', cc: action.cc || '', bcc: action.bcc || '', subject: action.subject || '', body: action.body || '', isHtml: action.isHtml !== false })
    : ({ id: action.id || `action-${index}`, type: 'webhook', name: action.name || 'Webhook Aksiyonu', profileId: String(action.profile_id || ''), method: action.method || 'POST', url: action.url || '', headersText: JSON.stringify(action.headers || { 'Content-Type': 'application/json' }, null, 2), bodyText: JSON.stringify(action.body || {}, null, 2) })) : [],
});

export function Automations() {
  const [rows, setRows] = useState<any[]>([]);
  const [logs, setLogs] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [form, setForm] = useState<AutomationForm>(defaultForm());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  const [activeSubTab, setActiveSubTab] = useState<'automations' | 'connections'>('automations');
  const [connectionProfiles, setConnectionProfiles] = useState<any[]>([]);
  const [expandedLogs, setExpandedLogs] = useState<Set<string>>(new Set());
  const [refreshingLogs, setRefreshingLogs] = useState(false);
  const [showTestRun, setShowTestRun] = useState(false);
  const [variableSearch, setVariableSearch] = useState('');
  const [copiedToken, setCopiedToken] = useState('');
  const [templateVariables, setTemplateVariables] = useState<Array<{ label: string; token: string }>>([]);
  const [testContext, setTestContext] = useState('{\n  "after": {\n    "shipment_no": "TEST-001",\n    "status": "yukleme-bekliyor",\n    "pickup_name": "Test Müşteri",\n    "delivery_name": "Test Teslimat"\n  },\n  "before": {\n    "status": "yeni"\n  }\n}');
  const [testResult, setTestResult] = useState<any>(null);
  const [testRunning, setTestRunning] = useState(false);

  const buildActionPayload = (action: ActionRow) => action.type === 'email'
    ? ({ type: 'email', name: action.name, profile_id: action.profileId ? Number(action.profileId) : undefined, to: action.to, cc: action.cc, bcc: action.bcc, subject: action.subject, body: action.body, isHtml: action.isHtml })
    : ({ type: 'webhook', name: action.name, profile_id: action.profileId ? Number(action.profileId) : undefined, method: action.method, url: action.url, headers: JSON.parse(action.headersText || '{}'), body: JSON.parse(action.bodyText || '{}') });

  const availableTemplateVariables = useMemo(() => templateVariables, [templateVariables]);

  const filteredTemplateVariables = useMemo(() => {
    const query = variableSearch.trim().toLocaleLowerCase('tr-TR');
    if (!query) return availableTemplateVariables;
    return availableTemplateVariables.filter((variable) => variable.label.toLocaleLowerCase('tr-TR').includes(query) || variable.token.toLocaleLowerCase('tr-TR').includes(query));
  }, [availableTemplateVariables, variableSearch]);

  const copyTemplateVariable = async (token: string) => {
    try {
      await navigator.clipboard.writeText(token);
      setCopiedToken(token);
      window.setTimeout(() => setCopiedToken((current) => current === token ? '' : current), 1500);
    } catch {
      alert('Değişken kopyalanamadı');
    }
  };

  const loadProfiles = async () => {
    try {
      const res = await connectionProfileService.getAll();
      setConnectionProfiles(Array.isArray(res?.data) ? res.data : []);
    } catch {}
  };

  const loadTemplateVariables = async (entityType: AutomationForm['entityType']) => {
    try {
      const res = await automationService.getTemplateVariables(entityType);
      setTemplateVariables(Array.isArray(res?.data) ? res.data : []);
    } catch {
      setTemplateVariables([]);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [automationRes, logRes]: any = await Promise.all([automationService.getAll(), automationService.getRuns(selectedId || undefined)]);
      const items = Array.isArray(automationRes?.data) ? automationRes.data : [];
      setRows(items);
      setLogs(Array.isArray(logRes?.data) ? logRes.data : []);
      if (items.length > 0) {
        const target = selectedId ? items.find((item: any) => String(item.id) === selectedId) || items[0] : items[0];
        setSelectedId(String(target.id));
        setForm(mapApiToForm(target));
      } else {
        setSelectedId(null);
        setForm(defaultForm());
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void loadData(); void loadProfiles(); void loadTemplateVariables('shipment'); }, []);

  useEffect(() => {
    setVariableSearch('');
    void loadTemplateVariables(form.entityType);
  }, [form.entityType]);

  const selectedLogs = useMemo(() => selectedId ? logs.filter((log) => String(log.automation_id) === String(selectedId)) : logs, [logs, selectedId]);
  const logCounts = useMemo(() => {
    const map: Record<string, number> = {};
    for (const log of logs) {
      const key = String(log.automation_id);
      map[key] = (map[key] || 0) + 1;
    }
    return map;
  }, [logs]);

  const refreshLogs = async () => {
    setRefreshingLogs(true);
    try {
      const res = await automationService.getRuns(selectedId || undefined);
      const newLogs = Array.isArray(res?.data) ? res.data : [];
      setLogs((prev) => {
        const merged = [...newLogs];
        const existingIds = new Set(newLogs.map((l: any) => l.id));
        for (const l of prev) if (!existingIds.has(l.id)) merged.push(l);
        return merged;
      });
    } finally {
      setRefreshingLogs(false);
    }
  };

  const saveAutomation = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        description: form.description,
        entity_type: form.entityType,
        trigger_event: form.triggerEvent,
        is_active: form.isActive,
        conditions: form.conditions.map((group) => ({ logic: group.logic, conditions: group.conditions.map(({ id, ...rest }) => rest) })),
        actions: form.actions.map(buildActionPayload),
      };

      if (form.id) await automationService.update(form.id, payload);
      else await automationService.create(payload);
      await loadData();
    } catch (error: any) {
      alert(error?.message || 'Otomasyon kaydedilirken hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const runTest = async () => {
    setTestRunning(true);
    setTestResult(null);
    try {
      let context = {};
      try { context = JSON.parse(testContext); } catch { alert('Geçersiz JSON formatı'); setTestRunning(false); return; }
      const payload = {
        entityType: form.entityType,
        actions: form.actions.map(buildActionPayload),
        conditions: form.conditions.map((group) => ({ logic: group.logic, conditions: group.conditions.map(({ id, ...rest }) => rest) })),
        context,
      };
      const res = await automationService.testRun(payload);
      setTestResult(res?.data || res);
    } catch (error: any) {
      setTestResult({ error: error?.message || 'Test çalıştırma hatası' });
    } finally {
      setTestRunning(false);
    }
  };

  const deleteAutomation = async () => {
    if (!form.id) return;
    if (!confirm('Bu otomasyonu silmek istediğinize emin misiniz?')) return;
    await automationService.delete(form.id);
    await loadData();
  };

  return (
    <div className="space-y-6 p-1">
      <div className="flex items-center justify-between rounded-3xl border border-slate-200 bg-white px-6 py-5 shadow-sm">
        <div>
          <div className="text-sm text-slate-500">Operasyon Yönetimi / Otomasyonlar</div>
          <h1 className="text-3xl font-bold text-slate-900">{form.name || 'Yeni Otomasyon'}</h1>
          <p className="mt-1 text-sm text-slate-500">Durum değişimleri ve sevkiyat olaylarına göre API ve mail otomasyonları oluşturun.</p>
        </div>
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => setShowLogs((current) => !current)} className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"><History className="h-4 w-4" />Geçmiş{selectedLogs.length > 0 ? <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-700">{selectedLogs.length}</span> : null}</button>
          <button type="button" onClick={refreshLogs} disabled={refreshingLogs} className="rounded-2xl border border-slate-200 p-2 text-sm text-slate-500 hover:bg-slate-50 disabled:opacity-50"><RefreshCw className={`h-4 w-4 ${refreshingLogs ? 'animate-spin' : ''}`} /></button>
          <label className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700">
            <span>{form.isActive ? 'Açık' : 'Kapalı'}</span>
            <input type="checkbox" checked={form.isActive} onChange={(e) => setForm((current) => ({ ...current, isActive: e.target.checked }))} />
          </label>
          <button type="button" onClick={() => setShowTestRun(true)} disabled={form.actions.length === 0} className="inline-flex items-center gap-2 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"><Activity className="h-4 w-4" />Test Çalıştır</button>
          <button type="button" onClick={saveAutomation} disabled={saving} className="inline-flex items-center gap-2 rounded-2xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"><Save className="h-4 w-4" />{saving ? 'Kaydediliyor' : 'Kaydet'}</button>
        </div>
      </div>

      <div className="flex gap-1 rounded-2xl border border-slate-200 bg-slate-100 p-1">
        <button type="button" onClick={() => setActiveSubTab('automations')} className={`flex-1 rounded-xl px-4 py-2.5 text-sm font-medium transition-colors ${activeSubTab === 'automations' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}><Activity className="mr-2 inline h-4 w-4" />Otomasyonlar</button>
        <button type="button" onClick={() => setActiveSubTab('connections')} className={`flex-1 rounded-xl px-4 py-2.5 text-sm font-medium transition-colors ${activeSubTab === 'connections' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}><Cable className="mr-2 inline h-4 w-4" />Bağlantılar</button>
      </div>

      {activeSubTab === 'connections' ? (
        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <ConnectionProfiles />
        </div>
      ) : (
      <div className="grid gap-6 xl:grid-cols-[320px_minmax(0,1fr)]">
        <aside className="space-y-4 rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
          <button type="button" onClick={() => { setSelectedId(null); setForm(defaultForm()); }} className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-blue-50 px-4 py-3 text-sm font-semibold text-blue-700 hover:bg-blue-100"><Plus className="h-4 w-4" />Yeni Otomasyon</button>
          <div className="space-y-2">
            {rows.map((row) => (
              <button key={row.id} type="button" onClick={() => { setSelectedId(String(row.id)); setForm(mapApiToForm(row)); }} className={`w-full rounded-2xl border px-4 py-3 text-left ${String(row.id) === selectedId ? 'border-blue-300 bg-blue-50' : 'border-slate-200 bg-white hover:bg-slate-50'}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="font-semibold text-slate-900">{row.name}</div>
                  <span className={`rounded-full px-2 py-1 text-xs font-medium ${row.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>{row.is_active ? 'Açık' : 'Kapalı'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="mt-1 text-xs text-slate-500">{(entityOptions.find((item) => item.value === row.entity_type)?.label || row.entity_type)} / {(triggerOptionsByEntity[row.entity_type as keyof typeof triggerOptionsByEntity] || []).find((item) => item.value === row.trigger_event)?.label || row.trigger_event}</div>
                  {logCounts[String(row.id)] > 0 && <span className="flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500"><History className="h-3 w-3" />{logCounts[String(row.id)]}</span>}
                </div>
              </button>
            ))}
          </div>
        </aside>

        <div className="space-y-6">
          <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="rounded-2xl bg-blue-100 p-4 text-blue-700"><Activity className="h-7 w-7" /></div>
                <div>
                  <h2 className="text-2xl font-bold text-slate-900">1. Tetikleyici</h2>
                  <p className="text-sm text-slate-500">Bu otomasyon hangi olayda çalışsın?</p>
                </div>
              </div>
            </div>
            <div className="mt-6 grid gap-4 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Otomasyon Adı</label>
                <input value={form.name} onChange={(e) => setForm((current) => ({ ...current, name: e.target.value }))} className="h-12 w-full rounded-2xl border border-slate-300 px-4 text-sm" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Tür</label>
                <select value={form.entityType} onChange={(e) => setForm((current) => ({ ...current, entityType: e.target.value as any, conditions: [], triggerEvent: triggerOptionsByEntity[e.target.value as keyof typeof triggerOptionsByEntity]?.[0]?.value || 'created' }))} className="h-12 w-full rounded-2xl border border-slate-300 px-4 text-sm">{entityOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Tetikleyici Olay</label>
                <select value={form.triggerEvent} onChange={(e) => setForm((current) => ({ ...current, triggerEvent: e.target.value as any }))} className="h-12 w-full rounded-2xl border border-slate-300 px-4 text-sm">{(triggerOptionsByEntity[form.entityType as keyof typeof triggerOptionsByEntity] || []).map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select>
              </div>
              <div className="md:col-span-2">
                <label className="mb-1 block text-sm font-medium text-slate-700">Açıklama</label>
                <textarea value={form.description} onChange={(e) => setForm((current) => ({ ...current, description: e.target.value }))} rows={3} className="w-full rounded-2xl border border-slate-300 px-4 py-3 text-sm" />
              </div>
            </div>
          </section>

          <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-slate-900">2. Koşullar</h2>
                <p className="text-sm text-slate-500">Koşul grupları ile AND/OR mantığında filtreleme yapın. Gruplar arası "VE" (AND) bağlıdır.</p>
              </div>
              <button type="button" onClick={() => setForm((current) => ({ ...current, conditions: [...current.conditions, createConditionGroup()] }))} className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-700 hover:bg-amber-100">Grup Ekle</button>
            </div>
            <div className="mt-5 space-y-6">
              {form.conditions.length === 0 && <div className="rounded-2xl border border-dashed border-slate-300 px-4 py-5 text-sm text-slate-500">Koşul grubu yok. Olay tetiklenince aksiyonlar doğrudan çalışır.</div>}
              {form.conditions.map((group, gi) => (
                <div key={group.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="mb-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="rounded-lg bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800">{gi === 0 ? 'KOŞUL 1' : `KOŞUL ${gi + 1}`}</span>
                      <select value={group.logic} onChange={(e) => setForm((current) => ({ ...current, conditions: current.conditions.map((g) => g.id === group.id ? { ...g, logic: e.target.value as any } : g) }))} className="h-8 rounded-lg border border-slate-300 px-2 text-xs font-medium"><option value="AND">TÜMÜ SAĞLANMALI (AND)</option><option value="OR">HERHANGİ BİRİ SAĞLANMALI (OR)</option></select>
                    </div>
                    <div className="flex gap-2">
                      <button type="button" onClick={() => setForm((current) => ({ ...current, conditions: current.conditions.map((g) => g.id === group.id ? { ...g, conditions: [...g.conditions, createCondition()] } : g) }))} className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700 hover:bg-amber-100">Koşul Ekle</button>
                      {form.conditions.length > 1 && <button type="button" onClick={() => setForm((current) => ({ ...current, conditions: current.conditions.filter((g) => g.id !== group.id) }))} className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-1 text-xs font-medium text-rose-700 hover:bg-rose-100"><Trash2 className="inline h-3 w-3" /> Grubu Sil</button>}
                    </div>
                  </div>
                  {group.conditions.length === 0 && <div className="rounded-xl border border-dashed border-slate-300 px-3 py-3 text-xs text-slate-500">Bu grupta koşul yok.</div>}
                  {group.conditions.map((condition) => (
                    <div key={condition.id} className="mb-2 grid gap-3 rounded-xl border border-slate-200 bg-white p-3 md:grid-cols-[100px_180px_160px_minmax(0,1fr)_44px]">
                      <select value={condition.source} onChange={(e) => setForm((current) => ({ ...current, conditions: current.conditions.map((g) => g.id === group.id ? { ...g, conditions: g.conditions.map((item) => item.id === condition.id ? { ...item, source: e.target.value as any } : item) } : g) }))} className="h-9 rounded-lg border border-slate-300 px-2 text-xs"><option value="after">Sonra</option><option value="before">Önce</option></select>
                      <select value={condition.field} onChange={(e) => setForm((current) => ({ ...current, conditions: current.conditions.map((g) => g.id === group.id ? { ...g, conditions: g.conditions.map((item) => item.id === condition.id ? { ...item, field: e.target.value } : item) } : g) }))} className="h-9 rounded-lg border border-slate-300 px-2 text-xs">{(conditionFieldsByEntity[form.entityType as keyof typeof conditionFieldsByEntity] || []).map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select>
                      <select value={condition.operator} onChange={(e) => setForm((current) => ({ ...current, conditions: current.conditions.map((g) => g.id === group.id ? { ...g, conditions: g.conditions.map((item) => item.id === condition.id ? { ...item, operator: e.target.value as any } : item) } : g) }))} className="h-9 rounded-lg border border-slate-300 px-2 text-xs"><option value="equals">eşittir</option><option value="not_equals">eşit değildir</option><option value="contains">içerir</option><option value="exists">dolu</option><option value="not_exists">boş</option><option value="changed">değişti</option><option value="changed_to">değişti ve oldu</option><option value="changed_from">şundan değişti</option></select>
                      <input value={condition.value} onChange={(e) => setForm((current) => ({ ...current, conditions: current.conditions.map((g) => g.id === group.id ? { ...g, conditions: g.conditions.map((item) => item.id === condition.id ? { ...item, value: e.target.value } : item) } : g) }))} placeholder="Değer" className="h-9 rounded-lg border border-slate-300 px-2 text-xs" />
                      <button type="button" onClick={() => setForm((current) => ({ ...current, conditions: current.conditions.map((g) => g.id === group.id ? { ...g, conditions: g.conditions.filter((item) => item.id !== condition.id) } : g) }))} className="inline-flex h-9 items-center justify-center rounded-lg border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100"><X className="h-3 w-3" /></button>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </section>

          <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-slate-900">3. Aksiyonlar</h2>
                <p className="text-sm text-slate-500">Webhook API veya mail aksiyonlarını dilediğiniz kadar ekleyin.</p>
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={() => setForm((current) => ({ ...current, actions: [...current.actions, createWebhookAction()] }))} className="inline-flex items-center gap-2 rounded-2xl border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100"><Webhook className="h-4 w-4" />Webhook</button>
                <button type="button" onClick={() => setForm((current) => ({ ...current, actions: [...current.actions, createEmailAction()] }))} className="inline-flex items-center gap-2 rounded-2xl border border-pink-200 bg-pink-50 px-4 py-2 text-sm font-medium text-pink-700 hover:bg-pink-100"><Mail className="h-4 w-4" />Mail</button>
              </div>
            </div>
            <div className="mt-5 space-y-5">
              {form.actions.length === 0 && <div className="rounded-2xl border border-dashed border-slate-300 px-4 py-5 text-sm text-slate-500">Henüz aksiyon eklenmedi.</div>}
              {form.actions.map((action, index) => (
                <div key={action.id} className="rounded-3xl border border-slate-200 p-5">
                  <div className="mb-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`rounded-2xl p-3 ${action.type === 'email' ? 'bg-pink-100 text-pink-700' : 'bg-blue-100 text-blue-700'}`}>{action.type === 'email' ? <Mail className="h-5 w-5" /> : <Webhook className="h-5 w-5" />}</div>
                      <div>
                        <div className="text-xl font-bold text-slate-900">3.{index + 1} {action.type === 'email' ? 'Mail ile Gönder' : 'Webhook Çağır'}</div>
                        <div className="text-sm text-slate-500"><code>{'{{after.shipment_no}}'}</code>, <code>{'{{after.status}}'}</code> gibi alanları kullanabilirsiniz.</div>
                      </div>
                    </div>
                    <button type="button" onClick={() => setForm((current) => ({ ...current, actions: current.actions.filter((item) => item.id !== action.id) }))} className="rounded-xl border border-rose-200 bg-rose-50 p-2 text-rose-700 hover:bg-rose-100"><X className="h-4 w-4" /></button>
                  </div>

                  {action.type === 'webhook' ? (
                    <div className="grid gap-4 md:grid-cols-2">
                      <div><label className="mb-1 block text-sm font-medium text-slate-700">Aksiyon Adı</label><input value={action.name} onChange={(e) => setForm((current) => ({ ...current, actions: current.actions.map((item) => item.id === action.id ? { ...item, name: e.target.value } : item) }))} className="h-11 w-full rounded-xl border border-slate-300 px-3 text-sm" /></div>
                      <div><label className="mb-1 block text-sm font-medium text-slate-700">Bağlantı Profili</label><select value={action.profileId} onChange={(e) => setForm((current) => ({ ...current, actions: current.actions.map((item) => item.id === action.id ? { ...item, profileId: e.target.value } : item) }))} className="h-11 w-full rounded-xl border border-slate-300 px-3 text-sm"><option value="">Profil seçilmedi (inline)</option>{connectionProfiles.filter((p) => p.type === 'webhook').map((p) => <option key={p.id} value={String(p.id)}>{p.name}</option>)}</select></div>
                      <div><label className="mb-1 block text-sm font-medium text-slate-700">Method</label><select value={action.method} onChange={(e) => setForm((current) => ({ ...current, actions: current.actions.map((item) => item.id === action.id ? { ...item, method: e.target.value as any } : item) }))} className="h-11 w-full rounded-xl border border-slate-300 px-3 text-sm"><option value="POST">POST</option><option value="PUT">PUT</option><option value="PATCH">PATCH</option><option value="GET">GET</option></select></div>
                      <div className="md:col-span-2"><label className="mb-1 block text-sm font-medium text-slate-700">URL</label><input value={action.url} onChange={(e) => setForm((current) => ({ ...current, actions: current.actions.map((item) => item.id === action.id ? { ...item, url: e.target.value } : item) }))} className="h-11 w-full rounded-xl border border-slate-300 px-3 text-sm" placeholder="https://..." /></div>
                      <div className="md:col-span-2 rounded-2xl border border-blue-200 bg-blue-50 p-4">
                        <div className="mb-2 text-sm font-medium text-blue-900">Kullanılabilir Değişkenler</div>
                        <div className="mb-3 text-xs text-blue-800">Aşağıdaki değişkenleri URL, header ve body JSON içinde kullanabilirsiniz.</div>
                        <div className="relative mb-3">
                          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-blue-400" />
                          <input value={variableSearch} onChange={(e) => setVariableSearch(e.target.value)} placeholder="Değişken ara..." className="h-10 w-full rounded-xl border border-blue-200 bg-white pl-10 pr-3 text-sm text-slate-700" />
                        </div>
                        <div className="grid gap-2 md:grid-cols-2">
                          {filteredTemplateVariables.map((variable) => (
                            <button type="button" key={variable.token} onClick={() => void copyTemplateVariable(variable.token)} className="rounded-xl border border-blue-100 bg-white px-3 py-2 text-left hover:border-blue-300 hover:bg-blue-100/40">
                              <div className="text-xs text-slate-500">{variable.label}</div>
                              <div className="mt-1 flex items-center justify-between gap-2">
                                <code className="text-xs text-blue-700">{variable.token}</code>
                                <span className={`inline-flex items-center gap-1 text-[11px] ${copiedToken === variable.token ? 'text-emerald-600' : 'text-blue-500'}`}><Copy className="h-3 w-3" />{copiedToken === variable.token ? 'Kopyalandı' : 'Kopyala'}</span>
                              </div>
                            </button>
                          ))}
                        </div>
                        {filteredTemplateVariables.length === 0 && <div className="mt-3 text-xs text-blue-700">Aramaya uygun değişken bulunamadı.</div>}
                      </div>
                      <div><label className="mb-1 block text-sm font-medium text-slate-700">Headers JSON</label><textarea value={action.headersText} onChange={(e) => setForm((current) => ({ ...current, actions: current.actions.map((item) => item.id === action.id ? { ...item, headersText: e.target.value } : item) }))} rows={8} className="w-full rounded-xl border border-slate-300 px-3 py-3 font-mono text-xs" /></div>
                      <div><label className="mb-1 block text-sm font-medium text-slate-700">Body JSON</label><textarea value={action.bodyText} onChange={(e) => setForm((current) => ({ ...current, actions: current.actions.map((item) => item.id === action.id ? { ...item, bodyText: e.target.value } : item) }))} rows={8} className="w-full rounded-xl border border-slate-300 px-3 py-3 font-mono text-xs" /></div>
                    </div>
                  ) : (
                    <div className="grid gap-4 md:grid-cols-2">
                      <div><label className="mb-1 block text-sm font-medium text-slate-700">Aksiyon Adı</label><input value={action.name} onChange={(e) => setForm((current) => ({ ...current, actions: current.actions.map((item) => item.id === action.id ? { ...item, name: e.target.value } : item) }))} className="h-11 w-full rounded-xl border border-slate-300 px-3 text-sm" /></div>
                      <div><label className="mb-1 block text-sm font-medium text-slate-700">Bağlantı Profili</label><select value={action.profileId} onChange={(e) => setForm((current) => ({ ...current, actions: current.actions.map((item) => item.id === action.id ? { ...item, profileId: e.target.value } : item) }))} className="h-11 w-full rounded-xl border border-slate-300 px-3 text-sm"><option value="">SMTP profili seçin</option>{connectionProfiles.filter((p) => p.type === 'smtp').map((p) => <option key={p.id} value={String(p.id)}>{p.name}</option>)}</select></div>
                      <div className="md:col-span-2 rounded-2xl border border-pink-100 bg-pink-50 px-4 py-3 text-xs text-pink-700">SMTP host, port, kullanıcı ve şifre bilgileri bağlantı profilinden alınır.</div>
                      <div className="md:col-span-2"><label className="mb-1 block text-sm font-medium text-slate-700">Kime</label><input value={action.to} onChange={(e) => setForm((current) => ({ ...current, actions: current.actions.map((item) => item.id === action.id ? { ...item, to: e.target.value } : item) }))} className="h-11 w-full rounded-xl border border-slate-300 px-3 text-sm" placeholder="a@firma.com, b@firma.com" /></div>
                      <div><label className="mb-1 block text-sm font-medium text-slate-700">CC</label><input value={action.cc} onChange={(e) => setForm((current) => ({ ...current, actions: current.actions.map((item) => item.id === action.id ? { ...item, cc: e.target.value } : item) }))} className="h-11 w-full rounded-xl border border-slate-300 px-3 text-sm" /></div>
                      <div><label className="mb-1 block text-sm font-medium text-slate-700">BCC</label><input value={action.bcc} onChange={(e) => setForm((current) => ({ ...current, actions: current.actions.map((item) => item.id === action.id ? { ...item, bcc: e.target.value } : item) }))} className="h-11 w-full rounded-xl border border-slate-300 px-3 text-sm" /></div>
                      <div className="md:col-span-2"><label className="mb-1 block text-sm font-medium text-slate-700">Konu</label><input value={action.subject} onChange={(e) => setForm((current) => ({ ...current, actions: current.actions.map((item) => item.id === action.id ? { ...item, subject: e.target.value } : item) }))} className="h-11 w-full rounded-xl border border-slate-300 px-3 text-sm" /></div>
                      <div className="md:col-span-2 flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                        <div>
                          <div className="text-sm font-medium text-slate-700">HTML Mail İçeriği</div>
                          <div className="text-xs text-slate-500">Doğrudan HTML yapıştırabilirsiniz. Yer tutucular için mevcut sözdizimi kullanılır: <code>{'{{after.shipment_no}}'}</code></div>
                        </div>
                        <label className="inline-flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={action.isHtml} onChange={(e) => setForm((current) => ({ ...current, actions: current.actions.map((item) => item.id === action.id ? { ...item, isHtml: e.target.checked } : item) }))} />HTML olarak gönder</label>
                      </div>
                      <div className="md:col-span-2 rounded-2xl border border-amber-200 bg-amber-50 p-4">
                        <div className="mb-2 text-sm font-medium text-amber-900">Kullanılabilir Değişkenler</div>
                        <div className="mb-3 text-xs text-amber-800">Aşağıdaki değişkenleri konu ve body içinde kullanabilirsiniz.</div>
                        <div className="relative mb-3">
                          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-amber-400" />
                          <input value={variableSearch} onChange={(e) => setVariableSearch(e.target.value)} placeholder="Değişken ara..." className="h-10 w-full rounded-xl border border-amber-200 bg-white pl-10 pr-3 text-sm text-slate-700" />
                        </div>
                        <div className="grid gap-2 md:grid-cols-2">
                          {filteredTemplateVariables.map((variable) => (
                            <button type="button" key={variable.token} onClick={() => void copyTemplateVariable(variable.token)} className="rounded-xl border border-amber-100 bg-white px-3 py-2 text-left hover:border-amber-300 hover:bg-amber-100/40">
                              <div className="text-xs text-slate-500">{variable.label}</div>
                              <div className="mt-1 flex items-center justify-between gap-2">
                                <code className="text-xs text-amber-700">{variable.token}</code>
                                <span className={`inline-flex items-center gap-1 text-[11px] ${copiedToken === variable.token ? 'text-emerald-600' : 'text-amber-600'}`}><Copy className="h-3 w-3" />{copiedToken === variable.token ? 'Kopyalandı' : 'Kopyala'}</span>
                              </div>
                            </button>
                          ))}
                        </div>
                        {filteredTemplateVariables.length === 0 && <div className="mt-3 text-xs text-amber-700">Aramaya uygun değişken bulunamadı.</div>}
                      </div>
                      <div className="md:col-span-2"><label className="mb-1 block text-sm font-medium text-slate-700">Mesaj / Body</label><textarea value={action.body} onChange={(e) => setForm((current) => ({ ...current, actions: current.actions.map((item) => item.id === action.id ? { ...item, body: e.target.value } : item) }))} rows={18} className={`w-full rounded-xl border border-slate-300 px-3 py-3 text-sm ${action.isHtml ? 'font-mono' : ''}`} /></div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>

          {showLogs && (
            <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-slate-900">Otomasyon Geçmişi</h2>
                <span className="text-sm text-slate-500">{selectedLogs.length} kayıt</span>
              </div>
              <div className="mt-4 space-y-3">
                {selectedLogs.map((log) => {
                  const expanded = expandedLogs.has(String(log.id));
                  return (
                  <div key={log.id} className="rounded-2xl border border-slate-200">
                    <button type="button" onClick={() => setExpandedLogs((prev) => { const next = new Set(prev); if (next.has(String(log.id))) next.delete(String(log.id)); else next.add(String(log.id)); return next; })} className="flex w-full items-center justify-between p-4 text-left hover:bg-slate-50">
                      <div className="flex items-center gap-3">
                        {expanded ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-slate-900">{log.trigger_event}</span>
                            <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${log.status === 'success' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>{log.status}</span>
                          </div>
                          <div className="text-xs text-slate-500">{new Date(log.created_at).toLocaleString('tr-TR')}</div>
                        </div>
                      </div>
                      {log.entity_type && <div className="text-xs text-slate-400">{log.entity_type}{log.entity_id ? ` #${log.entity_id}` : ''}</div>}
                    </button>
                    {expanded && (
                      <div className="border-t border-slate-100 px-4 pb-4 pt-3 space-y-3">
                        {log.error_message && <div className="rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700">{log.error_message}</div>}
                        {log.request_payload && (
                          <div>
                            <div className="mb-1 text-xs font-medium text-slate-500">İstek (Request Payload)</div>
                            <pre className="max-h-60 overflow-auto rounded-xl bg-slate-50 p-3 font-mono text-xs text-slate-700">{typeof log.request_payload === 'object' ? JSON.stringify(log.request_payload, null, 2) : String(log.request_payload)}</pre>
                          </div>
                        )}
                        {log.response_payload && (
                          <div>
                            <div className="mb-1 text-xs font-medium text-slate-500">Yanıt (Response Payload)</div>
                            <pre className="max-h-60 overflow-auto rounded-xl bg-slate-50 p-3 font-mono text-xs text-slate-700">{typeof log.response_payload === 'object' ? JSON.stringify(log.response_payload, null, 2) : String(log.response_payload)}</pre>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  );
                })}
                {selectedLogs.length === 0 && <div className="text-sm text-slate-500">Henüz log yok.</div>}
              </div>
            </section>
          )}
        </div>
      </div>
      )}
      {loading && <div className="text-sm text-slate-500">Yükleniyor...</div>}
      {activeSubTab === 'automations' && form.id && <button type="button" onClick={deleteAutomation} className="inline-flex items-center gap-2 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-2 text-sm font-medium text-rose-700 hover:bg-rose-100"><Trash2 className="h-4 w-4" />Otomasyonu Sil</button>}

      {showTestRun && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-3xl border border-slate-200 bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
              <h3 className="text-xl font-bold text-slate-900">Test Çalıştır</h3>
              <button type="button" onClick={() => { setShowTestRun(false); setTestResult(null); }} className="rounded-xl border border-slate-200 p-2 hover:bg-slate-50"><X className="h-4 w-4" /></button>
            </div>
            <div className="flex-1 overflow-y-auto space-y-4 p-6">
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Örnek Bağlam (JSON)</label>
                <p className="text-xs text-slate-500">Aksiyonların çalışacağı örnek `before` ve `after` verisini girin.</p>
                <textarea value={testContext} onChange={(e) => setTestContext(e.target.value)} rows={12} className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 font-mono text-xs" />
              </div>
              {testResult && (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <h4 className="font-semibold text-slate-900">Sonuç</h4>
                  <div className="mb-2 mt-2 flex items-center gap-2">
                    <span className="text-xs font-medium text-slate-500">Koşul Değerlendirme:</span>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${testResult.conditionResult ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{testResult.conditionResult ? 'GEÇTİ' : 'GEÇMEDİ'}</span>
                  </div>
                  <div className="space-y-2">
                    {(testResult.actionResults || []).map((ar: any, i: number) => (
                      <div key={i} className="rounded-xl border border-slate-200 bg-white p-3">
                        <div className="flex items-center justify-between"><span className="font-medium text-sm text-slate-900">{ar.name || ar.type}</span><span className={`rounded-full px-2 py-0.5 text-xs font-medium ${ar.success ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>{ar.success ? 'Başarılı' : 'Hata'}</span></div>
                        {ar.error && <div className="mt-1 text-sm text-rose-700">{ar.error}</div>}
                        {ar.success && ar.result && <pre className="mt-2 max-h-40 overflow-auto rounded-lg bg-slate-100 p-2 text-xs">{JSON.stringify(ar.result, null, 2)}</pre>}
                      </div>
                    ))}
                    {(!testResult.actionResults || testResult.actionResults.length === 0) && <div className="text-sm text-slate-500">Aksiyon sonucu yok.</div>}
                  </div>
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-slate-200 px-6 py-4">
              <button type="button" onClick={() => { setShowTestRun(false); setTestResult(null); }} className="rounded-2xl border border-slate-200 px-5 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50">Kapat</button>
              <button type="button" onClick={runTest} disabled={testRunning || form.actions.length === 0} className="inline-flex items-center gap-2 rounded-2xl bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"><Activity className="h-4 w-4" />{testRunning ? 'Çalışıyor...' : 'Testi Çalıştır'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
